import { useState, useEffect, useCallback } from 'react';
import { BillingBatchAPI } from '../apis/BillingBatchAPI';
import { 
    BillingBatchList, 
    BillingBatch, 
    IBillingBatchFilter,
    ICreateBillingBatchRequest
} from '../entities/TestKitOrder/BillingTypes';

/**
 * Step 42: React hooks for billing batch operations
 * Provides state management and API integration for billing batch functionality
 */

// Hook for listing billing batches with filtering and pagination
export const useBillingBatches = (initialFilter?: Partial<IBillingBatchFilter>) => {
    const [batches, setBatches] = useState<BillingBatchList | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<IBillingBatchFilter>({
        pageNumber: 1,
        pageSize: 25,
        sortBy: 'CreatedDateTime',
        sortDirection: 'DESC',
        ...initialFilter
    });

    const loadBatches = useCallback(async (newFilter?: Partial<IBillingBatchFilter>) => {
        try {
            setLoading(true);
            setError(null);
            
            const filterToUse = newFilter ? { ...filter, ...newFilter } : filter;
            const response = await BillingBatchAPI.getBillingBatches(filterToUse);
            setBatches(response);
            setFilter(filterToUse);
        } catch (err: any) {
            console.error('Failed to load billing batches:', err);
            setError(err?.message || 'Failed to load billing batches');
            setBatches(null);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    const refresh = useCallback(() => {
        loadBatches();
    }, [loadBatches]);

    const updateFilter = useCallback((newFilter: Partial<IBillingBatchFilter>) => {
        loadBatches(newFilter);
    }, [loadBatches]);

    const nextPage = useCallback(() => {
        if (batches && batches.hasNextPage) 
            updateFilter({ pageNumber: filter.pageNumber + 1 });
        
    }, [batches, filter.pageNumber, updateFilter]);

    const previousPage = useCallback(() => {
        if (batches && batches.hasPreviousPage) 
            updateFilter({ pageNumber: filter.pageNumber - 1 });
        
    }, [batches, filter.pageNumber, updateFilter]);

    const goToPage = useCallback((page: number) => {
        if (batches && page >= 1 && page <= batches.totalPages) 
            updateFilter({ pageNumber: page });
        
    }, [batches, updateFilter]);

    // Initial load
    useEffect(() => {
        loadBatches();
    }, []); // Only run on mount

    return {
        batches,
        loading,
        error,
        filter,
        refresh,
        updateFilter,
        nextPage,
        previousPage,
        goToPage,
        // Helper methods
        hasData: batches?.hasResults || false,
        totalCount: batches?.totalCount || 0,
        currentPage: batches?.currentPage || 1,
        totalPages: batches?.totalPages || 0,
    };
};

// Hook for getting a single billing batch by ID
export const useBillingBatch = (batchId: number | null) => {
    const [batch, setBatch] = useState<BillingBatch | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState<boolean>(false);

    const loadBatch = useCallback(async (id: number) => {
        try {
            setLoading(true);
            setError(null);
            setNotFound(false);
            
            const response = await BillingBatchAPI.getBillingBatch(id);
            if (response === null) {
                setNotFound(true);
                setBatch(null);
            } else {
                setBatch(response);
                setNotFound(false);
            }
        } catch (err: any) {
            console.error(`Failed to load billing batch ${id}:`, err);
            setError(err?.message || 'Failed to load billing batch');
            setBatch(null);
            setNotFound(false);
        } finally {
            setLoading(false);
        }
    }, []);

    const refresh = useCallback(() => {
        if (batchId) 
            loadBatch(batchId);
        
    }, [batchId, loadBatch]);

    // Load batch when batchId changes
    useEffect(() => {
        if (batchId) 
            loadBatch(batchId);
         else {
            setBatch(null);
            setError(null);
            setNotFound(false);
            setLoading(false);
        }
    }, [batchId, loadBatch]);

    return {
        batch,
        loading,
        error,
        notFound,
        refresh,
        // Helper properties
        hasData: batch !== null,
        orderCount: batch?.orderCount || 0,
        totalAmount: batch?.totalAmount || 0,
        formattedTotalAmount: batch?.formattedTotalAmount || '$0.00',
        batchNumber: batch?.batchNumber || '',
        csvFileName: batch?.csvFileName || '',
    };
};

// Hook for billing batch actions (create, delete, export)
export const useBillingBatchActions = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const createBatch = useCallback(async (request: ICreateBillingBatchRequest): Promise<{
        success: boolean;
        batchID?: number;
        batch?: BillingBatch;
        message: string;
    } | null> => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await BillingBatchAPI.createBillingBatch(request);
            return response;
        } catch (err: any) {
            console.error('Failed to create billing batch:', err);
            setError(err?.message || 'Failed to create billing batch');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createBatchAndExportCSV = useCallback(async (
        request: ICreateBillingBatchRequest,
        filename?: string
    ): Promise<boolean> => {
        try {
            setLoading(true);
            setError(null);
            
            const csvBlob = await BillingBatchAPI.createBillingBatchAndExportCSV(request);
            BillingBatchAPI.downloadCSVFile(csvBlob, filename);
            return true;
        } catch (err: any) {
            console.error('Failed to create and export billing batch:', err);
            setError(err?.message || 'Failed to create and export billing batch');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const exportBatchCSV = useCallback(async (
        batchId: number,
        filename?: string
    ): Promise<boolean> => {
        try {
            setLoading(true);
            setError(null);
            
            const csvBlob = await BillingBatchAPI.exportBillingBatchCSV(batchId);
            BillingBatchAPI.downloadCSVFile(csvBlob, filename);
            return true;
        } catch (err: any) {
            console.error(`Failed to export billing batch ${batchId}:`, err);
            setError(err?.message || 'Failed to export billing batch CSV');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteBatch = useCallback(async (batchId: number): Promise<{
        success: boolean;
        message: string;
    } | null> => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await BillingBatchAPI.deleteBillingBatch(batchId);
            return response;
        } catch (err: any) {
            console.error(`Failed to delete billing batch ${batchId}:`, err);
            setError(err?.message || 'Failed to delete billing batch');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        loading,
        error,
        createBatch,
        createBatchAndExportCSV,
        exportBatchCSV,
        deleteBatch,
        clearError,
    };
};

// Hook for getting recent batches (convenience hook)
export const useRecentBillingBatches = (pageSize: number = 10) => {
    const [batches, setBatches] = useState<BillingBatchList | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const loadRecentBatches = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await BillingBatchAPI.getRecentBatches(1, pageSize);
            setBatches(response);
        } catch (err: any) {
            console.error('Failed to load recent billing batches:', err);
            setError(err?.message || 'Failed to load recent billing batches');
            setBatches(null);
        } finally {
            setLoading(false);
        }
    }, [pageSize]);

    const refresh = useCallback(() => {
        loadRecentBatches();
    }, [loadRecentBatches]);

    // Initial load
    useEffect(() => {
        loadRecentBatches();
    }, [loadRecentBatches]);

    return {
        batches,
        loading,
        error,
        refresh,
        // Helper properties
        hasData: batches?.hasResults || false,
        recentBatches: batches?.batches || [],
    };
}; 